import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Flag, ShieldAlert, Check, Trash2, ExternalLink, Filter } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  post: "Feed post",
  comment: "Comment",
  testimony: "Testimony",
  memory: "Memory",
  user: "Member",
  message: "Direct message",
  directory: "Directory listing",
  inspiration: "Inspiration link",
};

const TYPE_LINK = (t, id) => {
  switch (t) {
    case "post": return `/feed#post-${id}`;
    case "testimony": return `/testimonies#t-${id}`;
    case "memory": return `/memories#m-${id}`;
    case "directory": return `/directory/${id}`;
    case "inspiration": return `/inspiration#i-${id}`;
    case "user": return `/u/${id}`;
    default: return null;
  }
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Admin Reports Panel — central inbox for every flag filed by members.
 * Lists each report with a target preview, lets the admin Dismiss
 * (just close the report) or Remove content (delete + close).
 * Hooked to GET /api/admin/reports and PATCH /api/admin/reports/:id.
 */
export default function AdminReportsPanel() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: { status: s, limit: 200 } });
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const openCount = useMemo(() => rows.filter((r) => r.status === "open").length, [rows]);

  const act = async (r, action) => {
    const confirmMsg =
      action === "remove_content"
        ? `Remove this ${TYPE_LABEL[r.target_type] || "item"} and close the report? This deletes the content permanently.`
        : "Dismiss this report and close it?";
    if (!window.confirm(confirmMsg)) return;
    setBusyId(r.id);
    try {
      await api.patch(`/admin/reports/${r.id}`, null, { params: { action } });
      // Optimistic remove from open list (or refresh when filter=all/resolved)
      if (status === "open") {
        setRows((rs) => rs.filter((x) => x.id !== r.id));
      } else {
        load(status);
      }
      toast.success(action === "remove_content" ? "Content removed and report closed" : "Report dismissed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update report");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="jj-card mt-6 p-6 sm:p-7 border-burgundy/30 bg-gradient-to-br from-cream to-cream-surface" data-testid="admin-reports-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-xl">
          <div className="overline text-burgundy flex items-center gap-2">
            <ShieldAlert className="h-3.5 w-3.5" /> Admin · Reports inbox
          </div>
          <h2 className="font-serif text-2xl text-jjblue mt-1" data-testid="admin-reports-heading">
            {loading ? "Loading…" :
              status === "open"
                ? `${openCount} open report${openCount === 1 ? "" : "s"}.`
                : `${rows.length} report${rows.length === 1 ? "" : "s"}.`}
          </h2>
          <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)]">
            Every flag from the family lands here — posts, comments, testimonies, memories, messages, listings, and members.
          </p>
        </div>

        <div className="inline-flex bg-cream-surface rounded-full p-1 text-xs" data-testid="admin-reports-tabs">
          {["open", "resolved", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${status === s ? "bg-burgundy text-cream" : "text-jjblue/70"}`}
              data-testid={`admin-reports-tab-${s}`}
            >
              <Filter className="h-3 w-3" /> {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="text-center text-[var(--jj-muted)] py-8 text-sm">Loading reports…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-[var(--jj-muted)] py-8 text-sm" data-testid="admin-reports-empty">
            {status === "open" ? "All caught up — no open flags." : "Nothing matches this filter."}
          </div>
        ) : (
          <ul className="space-y-2" data-testid="admin-reports-list">
            {rows.map((r) => {
              const preview = r.target_preview || {};
              const targetLink = TYPE_LINK(r.target_type, r.target_id);
              const missing = preview.missing;
              return (
                <li key={r.id} className="jj-card bg-white p-4" data-testid={`report-row-${r.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="h-9 w-9 rounded-xl bg-burgundy/10 text-burgundy flex items-center justify-center shrink-0">
                      <Flag className="h-4 w-4" strokeWidth={1.7} />
                    </div>

                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider bg-jjblue/10 text-jjblue px-2 py-0.5 rounded-full" data-testid={`report-type-${r.id}`}>
                          {TYPE_LABEL[r.target_type] || r.target_type}
                        </span>
                        {r.status === "resolved" && (
                          <span className="text-[10px] uppercase tracking-wider bg-gold/20 text-burgundy px-2 py-0.5 rounded-full">
                            {r.resolution === "remove_content" ? "removed" : "dismissed"}
                          </span>
                        )}
                        {missing && (
                          <span className="text-[10px] uppercase tracking-wider bg-[var(--jj-muted)]/15 text-[var(--jj-muted)] px-2 py-0.5 rounded-full">
                            content gone
                          </span>
                        )}
                        <span className="text-[11px] text-[var(--jj-muted)]">{timeAgo(r.created_at)}</span>
                      </div>

                      {/* Reporter + reason */}
                      <div className="mt-2 text-sm text-[var(--jj-text-secondary)]">
                        Flagged by{" "}
                        <Link to={`/u/${r.reporter_id}`} className="text-jjblue hover:text-burgundy hover:underline font-medium">
                          {r.reporter_name || "a member"}
                        </Link>
                        {r.reason && (<>: <span className="italic">&ldquo;{r.reason}&rdquo;</span></>)}
                      </div>

                      {/* Author + snippet */}
                      {!missing && (preview.snippet || preview.author_name) && (
                        <div className="mt-3 rounded-lg bg-cream-surface border border-jj p-3 text-sm">
                          {preview.author_name && (
                            <div className="text-[11px] uppercase tracking-wider text-[var(--jj-muted)] mb-1">
                              by{" "}
                              {preview.author_id ? (
                                <Link to={`/u/${preview.author_id}`} className="text-jjblue hover:text-burgundy hover:underline">
                                  {preview.author_name}
                                </Link>
                              ) : (
                                <span>{preview.author_name}</span>
                              )}
                            </div>
                          )}
                          {preview.snippet && (
                            <p className="text-jjblue/85 whitespace-pre-wrap break-words" data-testid={`report-snippet-${r.id}`}>
                              {preview.snippet}
                              {preview.snippet.length >= 280 && "…"}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {r.status === "open" && (
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        {targetLink && (
                          <Link
                            to={targetLink}
                            className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
                            data-testid={`report-view-${r.id}`}
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> View
                          </Link>
                        )}
                        <button
                          onClick={() => act(r, "dismiss")}
                          disabled={busyId === r.id}
                          className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
                          data-testid={`report-dismiss-${r.id}`}
                        >
                          <Check className="h-3.5 w-3.5" /> Dismiss
                        </button>
                        <button
                          onClick={() => act(r, "remove_content")}
                          disabled={busyId === r.id || missing}
                          className="text-xs !py-1.5 !px-3 inline-flex items-center gap-1 rounded-full bg-burgundy text-cream hover:bg-[var(--jj-burgundy-hover)] disabled:opacity-40"
                          data-testid={`report-remove-${r.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Remove
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
